// The email shapes the server's render tools return, checked before drawing.
// Fields are provider text: rendered as plain text, never as HTML.

export type EmailView = {
  id: string | null;
  threadId: string | null;
  from: string;
  to: string;
  cc: string;
  date: string;
  subject: string;
  body: string;
  bodyTruncated: boolean;
  attachments: { filename: string }[];
};

export type EmailThreadView = {
  threadId: string;
  subject: string;
  omitted: number;
  messages: EmailView[];
};

export type EmailDraftFields = {
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  body: string;
};

const text = (value: unknown) => (typeof value === 'string' ? value : '');

export function emailView(value: unknown): EmailView | null {
  if (!value || typeof value !== 'object') return null;
  const item = value as Record<string, unknown>;
  if (typeof item.body !== 'string') return null;
  return {
    id: text(item.id) || null,
    threadId: text(item.threadId) || null,
    from: text(item.from),
    to: text(item.to),
    cc: text(item.cc),
    date: text(item.date),
    subject: text(item.subject),
    body: item.body,
    bodyTruncated: item.bodyTruncated === true,
    attachments: Array.isArray(item.attachments)
      ? item.attachments
          .map((entry) => ({ filename: text((entry as { filename?: unknown })?.filename) }))
          .filter((entry) => entry.filename)
      : [],
  };
}

export function emailThreadView(value: unknown): EmailThreadView | null {
  if (!value || typeof value !== 'object') return null;
  const item = value as Record<string, unknown>;
  const messages = Array.isArray(item.messages)
    ? item.messages.map(emailView).filter((entry): entry is EmailView => entry !== null)
    : [];
  if (!messages.length) return null;
  return {
    threadId: text(item.threadId),
    subject: text(item.subject),
    omitted: typeof item.omitted === 'number' && item.omitted > 0 ? item.omitted : 0,
    messages,
  };
}

const list = (value: unknown) =>
  Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : [];

export function draftFields(args: Record<string, unknown>): EmailDraftFields {
  return {
    to: list(args.to),
    cc: list(args.cc),
    bcc: list(args.bcc),
    subject: text(args.subject),
    body: text(args.body),
  };
}

/** A header mailbox `Name <address>` split structurally for display. */
export function mailbox(value: string): { name: string; address: string } {
  const open = value.lastIndexOf('<');
  const close = value.lastIndexOf('>');
  if (open >= 0 && close > open) {
    const name = value.slice(0, open).trim().replace(/^"|"$/gu, '');
    const address = value.slice(open + 1, close).trim();
    return { name: name || address, address };
  }
  return { name: value.trim(), address: value.trim() };
}

// The same address format the server's schema (zod `email()`) accepts, so a
// draft the card lets through is never refused on Send.
const ADDRESS =
  /^(?!\.)(?!.*\.\.)([A-Za-z0-9_'+\-.]*)[A-Za-z0-9_+-]@([A-Za-z0-9][A-Za-z0-9-]*\.)+[A-Za-z]{2,}$/u;
export const validAddress = (value: string) => value.length <= 320 && ADDRESS.test(value);

/** Split a typed or pasted address list on its separators. */
export const addressTokens = (value: string) =>
  value
    .split(/[\s,;]+/u)
    .map((token) => token.trim())
    .filter(Boolean);

export function formatEmailDate(value: string, locale: string) {
  const time = Date.parse(value);
  if (!Number.isFinite(time)) return value;
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
      time,
    );
  } catch {
    return value;
  }
}
